import { useState, useRef } from 'react'
import { Upload, X, Image as ImageIcon, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'

interface ImageUploadProps {
  onImageUploaded: (imageUrl: string) => void
  onImageRemoved: (imageUrl: string) => void
  existingImages?: string[]
  maxImages?: number
  disabled?: boolean
  className?: string
}

export function ImageUpload({ 
  onImageUploaded, 
  onImageRemoved, 
  existingImages = [], 
  maxImages = 5,
  disabled = false,
  className = '' 
}: ImageUploadProps) {
  const [uploading, setUploading] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const canAddMore = existingImages.length < maxImages

  const handleFileSelect = async (files: FileList | null) => {
    if (!files || files.length === 0 || disabled || !canAddMore) return

    const file = files[0]
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Alleen afbeeldingen zijn toegestaan')
      return
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Afbeelding mag maximaal 5MB zijn')
      return
    }

    setUploading(true)
    
    try {
      // Convert to base64 for now (in production, you'd upload to a storage service)
      const reader = new FileReader()
      reader.onload = (e) => {
        const result = e.target?.result as string
        onImageUploaded(result)
        setUploading(false)
      }
      reader.onerror = () => {
        alert('Fout bij het laden van de afbeelding')
        setUploading(false)
      }
      reader.readAsDataURL(file)
    } catch (error) {
      console.error('Upload error:', error)
      alert('Fout bij het uploaden van de afbeelding')
      setUploading(false)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    handleFileSelect(e.dataTransfer.files)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Upload Area */}
      {canAddMore && (
        <Card 
          className={`border-2 border-dashed transition-colors cursor-pointer ${
            dragOver 
              ? 'border-orange-400 bg-orange-50' 
              : 'border-gray-300 hover:border-orange-300'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          onClick={() => !disabled && fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <div className="p-6 text-center">
            {uploading ? (
              <div className="flex flex-col items-center">
                <Loader2 className="h-8 w-8 text-orange-500 animate-spin mb-2" />
                <p className="text-sm text-gray-600">Uploaden...</p>
              </div>
            ) : (
              <div className="flex flex-col items-center">
                <Upload className="h-8 w-8 text-gray-400 mb-2" />
                <p className="text-sm font-medium text-gray-900 mb-1">
                  Klik om een foto te uploaden
                </p>
                <p className="text-xs text-gray-500">
                  Of sleep een afbeelding hierheen
                </p>
                <p className="text-xs text-gray-400 mt-2">
                  PNG, JPG tot 5MB ({existingImages.length}/{maxImages})
                </p>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={(e) => handleFileSelect(e.target.files)}
        className="hidden"
        disabled={disabled || !canAddMore}
      />

      {/* Existing Images Grid */}
      {existingImages.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {existingImages.map((imageUrl, index) => (
            <div key={index} className="relative group">
              <Card className="overflow-hidden">
                <div className="aspect-square relative">
                  <img
                    src={imageUrl}
                    alt={`Uploaded ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                  {!disabled && (
                    <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => onImageRemoved(imageUrl)}
                        className="rounded-full w-8 h-8 p-0"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </Card>
            </div>
          ))}
        </div>
      )}

      {/* Max Images Reached */}
      {!canAddMore && (
        <Card className="border-amber-200 bg-amber-50">
          <div className="p-4 text-center">
            <ImageIcon className="h-6 w-6 text-amber-600 mx-auto mb-2" />
            <p className="text-sm text-amber-800">
              Maximaal {maxImages} afbeeldingen toegestaan
            </p>
          </div>
        </Card>
      )}
    </div>
  )
}